// Machine Master — Feb 28 2026.
//
// Three-field registry of shop-floor machines (Machine Number, Type,
// Machine Hour Rate). Feeds Production Planning dropdown; Type + Rate
// are read-only in Production Planning and inherited from here.

import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import Layout from "../components/Layout";
import MasterExcelToolbar from "../components/MasterExcelToolbar";
import { Plus, PencilSimple, Trash, Wrench, MagnifyingGlass, Gear } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const blankForm = () => ({ machine_number: "", machine_type: "", machine_group: "", machine_hour_rate: "" });

export default function MachineMaster() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(blankForm());
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Inline "Add new type" dialog — stacks over the primary modal so the
  // operator never leaves the Add-Machine flow just to register a new
  // machine class (VTL, HMC, Grinder, etc.).
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [newType, setNewType] = useState("");
  const [manageTypesOpen, setManageTypesOpen] = useState(false);
  // Same pattern for machine Groups (Group 1..6 seeded, custom allowed).
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [newGroup, setNewGroup] = useState("");
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [mr, tr, gr] = await Promise.all([
        axios.get(`${API}/machine-master`),
        axios.get(`${API}/machine-types`),
        axios.get(`${API}/machine-groups`),
      ]);
      setRows(mr.data || []);
      setTypes(tr.data || []);
      setGroups(gr.data || []);
    } catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Default `machine_type` + `machine_group` when we open the form fresh
  // so the selects aren't blank on first paint.
  const openAdd = () => {
    setEditingId(null);
    setForm({
      ...blankForm(),
      machine_type: types[0]?.name || "",
      machine_group: groups[0]?.name || "",
    });
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      machine_number: r.machine_number || "",
      machine_type: r.machine_type || "CNC",
      machine_group: r.machine_group || "",
      machine_hour_rate: r.machine_hour_rate ?? "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.machine_number.trim()) return toast.error("Machine Number is required");
    if (!form.machine_type.trim()) return toast.error("Type of Machine is required");
    const mhr = parseFloat(form.machine_hour_rate);
    if (isNaN(mhr) || mhr < 0) return toast.error("Machine Hour Rate must be a non-negative number");
    const payload = {
      machine_number: form.machine_number.trim(),
      machine_type: form.machine_type.trim().toUpperCase(),
      machine_group: (form.machine_group || "").trim(),
      machine_hour_rate: mhr,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/machine-master/${editingId}`, payload);
        toast.success("Machine updated");
      } else {
        await axios.post(`${API}/machine-master`, payload);
        toast.success("Machine added");
      }
      setDialogOpen(false); setForm(blankForm()); setEditingId(null); load();
      window.dispatchEvent(new Event("machines:changed"));
    } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || e.message)); }
  };

  const addType = async () => {
    const name = newType.trim().toUpperCase();
    if (!name) return toast.error("Type name required");
    try {
      await axios.post(`${API}/machine-types`, { name });
      toast.success(`Added type '${name}'`);
      const tr = await axios.get(`${API}/machine-types`);
      setTypes(tr.data || []);
      setForm(f => ({ ...f, machine_type: name }));
      setNewType(""); setTypeDialogOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add type"); }
  };

  const removeType = async (id, name) => {
    if (!window.confirm(`Delete machine type '${name}'?`)) return;
    try {
      await axios.delete(`${API}/machine-types/${id}`);
      toast.success("Type deleted");
      const tr = await axios.get(`${API}/machine-types`);
      setTypes(tr.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const addGroup = async () => {
    const name = newGroup.trim();
    if (!name) return toast.error("Group name required");
    try {
      await axios.post(`${API}/machine-groups`, { name });
      toast.success(`Added group '${name}'`);
      const gr = await axios.get(`${API}/machine-groups`);
      setGroups(gr.data || []);
      setForm(f => ({ ...f, machine_group: name }));
      setNewGroup(""); setGroupDialogOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add group"); }
  };

  const removeGroup = async (id, name) => {
    if (!window.confirm(`Delete group '${name}'?`)) return;
    try {
      await axios.delete(`${API}/machine-groups/${id}`);
      toast.success("Group deleted");
      const gr = await axios.get(`${API}/machine-groups`);
      setGroups(gr.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this machine?")) return;
    try {
      await axios.delete(`${API}/machine-master/${id}`);
      toast.success("Deleted"); load();
      window.dispatchEvent(new Event("machines:changed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const filtered = rows.filter(r => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.machine_number || "").toLowerCase().includes(q) ||
           (r.machine_type || "").toLowerCase().includes(q);
  });

  return (
    <Layout title="Machine Master">
      <div className="p-6 space-y-5 max-w-[1200px] mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[#1D2951]/5 text-[#1D2951]">
              <Wrench size={20} weight="fill" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: "Chivo" }}>Machine Master</h1>
              <p className="text-xs text-[#64748B] mt-0.5 max-w-2xl">
                Shop-floor machine registry. Referenced by Production Planning to compute per-operation cost.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <MasterExcelToolbar basePath="/machine-master" entityLabel="Machines"
              onImportDone={load} size="sm" />
            <Button variant="outline" className="h-9 gap-1"
              onClick={() => setManageTypesOpen(true)} data-testid="mm-manage-types">
              <Gear size={14} /> Manage Types
            </Button>
            <Button variant="outline" className="h-9 gap-1"
              onClick={() => setManageGroupsOpen(true)} data-testid="mm-manage-groups">
              <Gear size={14} /> Manage Groups
            </Button>
            <Button className="bg-[#1D2951] hover:bg-[#2A3B73] text-white gap-1 h-9"
              onClick={openAdd} data-testid="mm-add-btn">
              <Plus size={14} /> Add Machine
            </Button>
          </div>
        </div>

        <Card className="p-2 border border-[#E2E8F0] flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by machine number or type…"
              className="min-h-[38px] pl-9 border-transparent focus-visible:border-[#CBD5E1]"
              data-testid="mm-search" />
          </div>
          <span className="text-[11px] text-[#64748B] font-semibold pr-2">
            {filtered.length} / {rows.length} machines
          </span>
        </Card>

        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="mm-table">
              <thead className="bg-[#F8FAFC] text-[#475569] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Machine Number</th>
                  <th className="px-3 py-3 text-left font-semibold">Type of Machine</th>
                  <th className="px-3 py-3 text-left font-semibold">Group</th>
                  <th className="px-3 py-3 text-right font-semibold">Machine Hour Rate (₹)</th>
                  <th className="px-3 py-3 text-center font-semibold w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {loading && <tr><td colSpan={5} className="py-10 text-center text-[#64748B]">Loading…</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={5} className="py-12 text-center text-[#94A3B8]">
                    No machines yet — click <b>Add Machine</b> to create your first one.
                  </td></tr>
                )}
                {filtered.map(r => (
                  <tr key={r.id} className="hover:bg-[#F8FAFC]" data-testid={`mm-row-${r.machine_number}`}>
                    <td className="px-4 py-3 font-mono font-semibold text-[#0F172A]">{r.machine_number}</td>
                    <td className="px-3 py-3">
                      {(() => {
                        const t = (r.machine_type || "").toUpperCase();
                        const palette = {
                          CNC: "bg-[#DBEAFE] text-[#1D4ED8]",
                          VMC: "bg-[#FCE7F3] text-[#9D174D]",
                          VTL: "bg-[#DCFCE7] text-[#166534]",
                        };
                        const cls = palette[t] || "bg-[#F1F5F9] text-[#334155]";
                        return (
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${cls}`}>
                            {t || "—"}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3 text-[#334155] text-xs">{r.machine_group || "—"}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#0F172A]">
                      ₹ {Number(r.machine_hour_rate || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-3 text-center whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`mm-edit-${r.machine_number}`}>
                        <PencilSimple size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-[#DC2626]"
                        data-testid={`mm-del-${r.machine_number}`}>
                        <Trash size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingId ? "Edit Machine" : "Add Machine"}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-[11px] font-semibold text-[#334155]">Machine Number *</Label>
              <Input value={form.machine_number}
                onChange={e => setForm({ ...form, machine_number: e.target.value })}
                placeholder="e.g. CNC-01, VMC-04"
                className="font-mono mt-0.5"
                data-testid="mm-machine-number" />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-[#334155]">Type of Machine *</Label>
              <div className="flex gap-1 mt-0.5">
                <select value={form.machine_type}
                  onChange={e => setForm({ ...form, machine_type: e.target.value })}
                  className="flex-1 min-h-[38px] border border-[#E2E8F0] rounded px-2 text-xs bg-white"
                  data-testid="mm-machine-type">
                  <option value="">— Pick a type —</option>
                  {types.map(t => (
                    <option key={t.id || t.name} value={t.name}>{t.name}</option>
                  ))}
                </select>
                <Button size="sm" variant="outline"
                  onClick={() => { setNewType(""); setTypeDialogOpen(true); }}
                  title="Add a new machine type"
                  data-testid="mm-add-type-inline">
                  <Plus size={14} />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-[#334155]">Group</Label>
              <div className="flex gap-1 mt-0.5">
                <select value={form.machine_group}
                  onChange={e => setForm({ ...form, machine_group: e.target.value })}
                  className="flex-1 min-h-[38px] border border-[#E2E8F0] rounded px-2 text-xs bg-white"
                  data-testid="mm-machine-group">
                  <option value="">— No group —</option>
                  {groups.map(g => (
                    <option key={g.id || g.name} value={g.name}>{g.name}</option>
                  ))}
                </select>
                <Button size="sm" variant="outline"
                  onClick={() => { setNewGroup(""); setGroupDialogOpen(true); }}
                  title="Add a new machine group"
                  data-testid="mm-add-group-inline">
                  <Plus size={14} />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-[#334155]">Machine Hour Rate (₹) *</Label>
              <Input type="number" step="any" min="0" value={form.machine_hour_rate}
                onChange={e => setForm({ ...form, machine_hour_rate: e.target.value })}
                placeholder="e.g. 350"
                className="font-mono mt-0.5"
                data-testid="mm-machine-hour-rate" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-[#E2E8F0] mt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#1D2951] text-white" onClick={save} data-testid="mm-save">
              <Plus size={14} /> {editingId ? "Save Changes" : "Add Machine"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={typeDialogOpen} onOpenChange={setTypeDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Machine Type</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            <div>
              <Label className="text-[11px] font-semibold text-[#334155]">Name *</Label>
              <Input value={newType}
                onChange={e => setNewType(e.target.value.toUpperCase())}
                placeholder="e.g. VTL, HMC, GRINDER"
                className="font-mono mt-0.5"
                data-testid="mm-new-type-name" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setTypeDialogOpen(false)}>Cancel</Button>
              <Button className="bg-[#1D2951] text-white" onClick={addType} data-testid="mm-new-type-save">
                <Plus size={14} /> Add Type
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={manageTypesOpen} onOpenChange={setManageTypesOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Manage Machine Types</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            <p className="text-[11px] text-[#64748B]">
              These types feed the &quot;Type of Machine&quot; dropdown when adding a new machine.
              A type in use by any machine cannot be deleted.
            </p>
            <div className="border border-[#E2E8F0] rounded overflow-hidden">
              <table className="w-full text-sm" data-testid="mm-types-table">
                <thead className="bg-[#F8FAFC] text-[#475569] text-[11px] uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Name</th>
                    <th className="px-3 py-2 text-right font-semibold w-16">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {types.length === 0 && (
                    <tr><td colSpan={2} className="py-6 text-center text-[#94A3B8]">No types yet.</td></tr>
                  )}
                  {types.map(t => (
                    <tr key={t.id || t.name}>
                      <td className="px-3 py-2 font-mono text-[#0F172A]">{t.name}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => removeType(t.id, t.name)}
                          className="text-[#DC2626]" data-testid={`mm-type-del-${t.name}`}>
                          <Trash size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between items-center pt-1">
              <Button variant="outline" onClick={() => { setNewType(""); setTypeDialogOpen(true); }}
                data-testid="mm-manage-add-type">
                <Plus size={14} /> Add Type
              </Button>
              <Button className="bg-[#1D2951] text-white" onClick={() => setManageTypesOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Machine Group</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            <div>
              <Label className="text-[11px] font-semibold text-[#334155]">Name *</Label>
              <Input value={newGroup}
                onChange={e => setNewGroup(e.target.value)}
                placeholder="e.g. Group 7, Line A, Bay-2"
                className="mt-0.5"
                data-testid="mm-new-group-name" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
              <Button className="bg-[#1D2951] text-white" onClick={addGroup} data-testid="mm-new-group-save">
                <Plus size={14} /> Add Group
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={manageGroupsOpen} onOpenChange={setManageGroupsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Manage Machine Groups</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            <p className="text-[11px] text-[#64748B]">
              These groups feed the &quot;Group&quot; dropdown when adding a new machine.
              A group in use by any machine cannot be deleted.
            </p>
            <div className="border border-[#E2E8F0] rounded overflow-hidden">
              <table className="w-full text-sm" data-testid="mm-groups-table">
                <thead className="bg-[#F8FAFC] text-[#475569] text-[11px] uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Name</th>
                    <th className="px-3 py-2 text-right font-semibold w-16">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {groups.length === 0 && (
                    <tr><td colSpan={2} className="py-6 text-center text-[#94A3B8]">No groups yet.</td></tr>
                  )}
                  {groups.map(g => (
                    <tr key={g.id || g.name}>
                      <td className="px-3 py-2 text-[#0F172A]">{g.name}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => removeGroup(g.id, g.name)}
                          className="text-[#DC2626]" data-testid={`mm-group-del-${g.name}`}>
                          <Trash size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between items-center pt-1">
              <Button variant="outline" onClick={() => { setNewGroup(""); setGroupDialogOpen(true); }}
                data-testid="mm-manage-add-group">
                <Plus size={14} /> Add Group
              </Button>
              <Button className="bg-[#1D2951] text-white" onClick={() => setManageGroupsOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
